import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldAlertIcon, BugIcon, PackageOpenIcon, ActivityIcon, FlameIcon, DatabaseIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteBeveiligingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website beveiliging | Bescherm je website | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website beveiliging: bescherm je website tegen hackers en malware. Tips over SSL, updates, sterke wachtwoorden en meer. Praktisch advies van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website beveiliging | Bescherm je website | Noord',
      'og:description': 'Website beveiliging: bescherm je website tegen hackers en malware. Tips over SSL, updates, sterke wachtwoorden en meer.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-beveiliging',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe beveilig je een website?',
    a: 'Een website beveiligen begint met een SSL-certificaat, sterke wachtwoorden en regelmatige updates van je CMS en plugins. Daarnaast is het belangrijk om een firewall in te stellen, dagelijkse backups te maken en verdachte activiteiten te monitoren. Wij nemen al deze stappen standaard mee in onze website beveiliging service.'
  }, {
    q: 'Is WordPress veilig?',
    a: "WordPress is van zichzelf een veilig platform, mits het goed wordt onderhouden. De meeste beveiligingsproblemen ontstaan door verouderde plugins, zwakke wachtwoorden of onbetrouwbare thema's. Met professionele WordPress beveiliging, regelmatige updates en monitoring is WordPress een uitstekende en veilige keuze voor je bedrijfswebsite."
  }, {
    q: 'Hoe vaak moeten beveiligingsupdates worden uitgevoerd?',
    a: "Kritieke beveiligingsupdates voeren wij direct door zodra ze beschikbaar zijn. Reguliere updates van WordPress core, thema's en plugins worden wekelijks gecontroleerd en maandelijks doorgevoerd na een grondige test. Zo blijft je website altijd beschermd tegen de nieuwste dreigingen."
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1857" data-mp="207ezx23dccl2">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1858" data-mp="2086waf4siece">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1859" data-mp="1cim2t2b0fyov">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1860" data-mp="17j9f6fcbenl8" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1861" data-mp="1ljk77lpd287z" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1862" data-mp="1hfcivpmteuaa" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1863" data-mp="3o7kevopixa2p">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1864" data-mp="3s84d6vlu2jqf">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1865" data-mp="108a6os6ixr7">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1866" data-mp="b0ioydtv4d8h">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-1867" data-mp="axmooiujlcgv">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-1868" data-mp="jskkpyszl5cf">/</span>
            <span className="text-accent-light font-medium" data-id="element-1869" data-mp="ornicy6n09ad">
              Website beveiliging
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1870" data-mp="tri7oxarkqtb">
            Website beveiliging voor bedrijven
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1871" data-mp="2j7vo79ucfo2k">
            Bescherm je website tegen hacks en malware. Wij zorgen voor
            professionele website security zodat jij zorgeloos kunt ondernemen.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1872" data-mp="24a39ul0j95py">
            <a href="#beveiliging-info" onClick={e => scrollToSection(e, 'beveiliging-info')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1873" data-mp="24bd7j93h4bw2">
              Bekijk beveiliging
              <ArrowRightIcon className="w-5 h-5" data-id="element-1874" data-mp="2n9pjf2lrrzvc" />
            </a>
          </div>
        </div>
      </section>

      {/* Belangrijkste risico's */}
      <section id="beveiliging-info" className="py-24 bg-surface-muted" data-id="element-1875" data-mp="3kqelmxemt459">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1876" data-mp="3fwtlaypkfy2w">
          <div className="text-center mb-16" data-id="element-1877" data-mp="1dzjdglpf9eea">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1878" data-mp="1e0upf8t0tsgh">
              Belangrijkste risico's zonder website beveiliging
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1879" data-mp="14b1du7svpdc8">
              Zonder goede beveiliging is je website kwetsbaar voor aanvallen
              die je bedrijf ernstig kunnen schaden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1880" data-mp="v80cekeykaew">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1881" data-mp="zbf07x951v2w">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-6" data-id="element-1882" data-mp="lbdrxwqjnlde">
                <ShieldAlertIcon className="w-6 h-6 text-red-500" data-id="element-1883" data-mp="qagsp4zhr5fk" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1884" data-mp="qcbgqs627dnv">Hacks</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1885" data-mp="2fq7vze5empti">
                Hackers kunnen toegang krijgen tot je website, gevoelige
                gegevens stelen of je site gebruiken voor kwaadaardige
                doeleinden. Dit schaadt je reputatie en het vertrouwen van je
                klanten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1886" data-mp="iqpkq8guogff">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-6" data-id="element-1887" data-mp="it25etdzt4w6">
                <BugIcon className="w-6 h-6 text-red-500" data-id="element-1888" data-mp="dyeigyyn0ijp" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1889" data-mp="rpt1v6wgilra">Malware</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1890" data-mp="mrri0jr7iier">
                Malware kan ongemerkt op je website worden geplaatst en
                bezoekers infecteren. Google kan je website vervolgens
                blokkeren, waardoor je vindbaarheid volledig wegvalt.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1891" data-mp="33hq9z45i8887">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-6" data-id="element-1892" data-mp="2yivildth1bch">
                <PackageOpenIcon className="w-6 h-6 text-red-500" data-id="element-1893" data-mp="2cppscmo8qp54" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1894" data-mp="2hnhjvfkz498s">
                Verouderde plugins
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1895" data-mp="2mfslcmvd433g">
                Verouderde WordPress plugins en thema's bevatten bekende
                beveiligingslekken. Zonder regelmatige updates is je website een
                makkelijk doelwit voor aanvallers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hoe wij beveiligen */}
      <section className="py-24 bg-white" data-id="element-1896" data-mp="23nqaxt9bdj1n">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1897" data-mp="23myfcifpvlbd">
          <div className="text-center mb-16" data-id="element-1898" data-mp="27q3f4d8ex8wb">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1899" data-mp="27nqqjh85g0aa">
              Hoe wij jouw website beveiligen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1900" data-mp="3j4gxx3ob6y5h">
              Onze website beveiliging aanpak beschermt je site op meerdere
              niveaus tegen alle bekende dreigingen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1901" data-mp="13hcvdrpzazz8">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1902" data-mp="yotcqgcfpenk">
              <div className="flex items-start gap-4" data-id="element-1903" data-mp="tp728enzqcig">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1904" data-mp="toff7s8gln3s">
                  <ActivityIcon className="w-6 h-6 text-accent" data-id="element-1905" data-mp="jsc3pvrpwc46" />
                </div>
                <div data-id="element-1906" data-mp="fqh6b2cwqivo">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1907" data-mp="fpz2nknx12dq">
                    Security monitoring
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1908" data-mp="asyvf6fbxtl1">
                    Wij monitoren je website 24/7 op verdachte activiteiten,
                    ongeautoriseerde inlogpogingen en bekende kwetsbaarheden.
                    Bij een dreiging grijpen wij direct in.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1909" data-mp="3cbb03njy0kzp">
              <div className="flex items-start gap-4" data-id="element-1910" data-mp="3qasow2x8ny92">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1911" data-mp="3q86k2b1qfwuj">
                  <FlameIcon className="w-6 h-6 text-accent" data-id="element-1912" data-mp="2xllthct2zos1" />
                </div>
                <div data-id="element-1913" data-mp="2tka1j0mqirgo">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1914" data-mp="2tktj2g8h3du6">
                    Firewall
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1915" data-mp="37cq5q4rdgvj6">
                    Een professionele web application firewall blokkeert
                    kwaadaardig verkeer voordat het je website bereikt.
                    Bescherming tegen brute force aanvallen, SQL-injecties en
                    meer.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1916" data-mp="pd78nidyk91z">
              <div className="flex items-start gap-4" data-id="element-1917" data-mp="u5rzcb1ki61a">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1918" data-mp="gdtwghyg96dk">
                  <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1919" data-mp="lbvh37bixdr5" />
                </div>
                <div data-id="element-1920" data-mp="191przjca8s8r">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1921" data-mp="193aohei5ql99">
                    Backups
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1922" data-mp="1d3b2iy1cgufs">
                    Dagelijkse automatische backups van je volledige website en
                    database. Mocht er iets misgaan, dan herstellen wij je
                    website snel naar een werkende versie.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1923" data-mp="3otwwq2n8fsrv">
              <div className="flex items-start gap-4" data-id="element-1924" data-mp="34q7o2jqmj33a">
                <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-1925" data-mp="34t3pwj5lebx0">
                  <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-1926" data-mp="2vyp6q5hxyh8m" />
                </div>
                <div data-id="element-1927" data-mp="2qzvuegee4wa1">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1928" data-mp="2r0nr5ujq501w">
                    Updates
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1929" data-mp="2m1kjs3g9uhqr">
                    Regelmatige updates van WordPress core, plugins en thema's.
                    Wij testen elke update grondig voordat deze live gaat om
                    compatibiliteitsproblemen te voorkomen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-1930" data-mp="b6dp065al441">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1931" data-mp="6aestwdq2ht2">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1932" data-mp="dsd2q1ztfuwt">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1933" data-mp="iqekmjli9o6p">
            <a href="#/website-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1934" data-mp="niplpprzfg0u">
              Website onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1935" data-mp="3r1929v1asxft">•</span>
            <a href="#/wordpress-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1936" data-mp="3vxzh6f1d9mzn">
              WordPress onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1937" data-mp="8uup3yausrih">•</span>
            <a href="#/website-hosting" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1938" data-mp="1g869290qw6q7">
              Website hosting
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-1939" data-mp="1dszs41i1t06t">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1940" data-mp="1du9qkrstcadz">
          <div className="text-center mb-16" data-id="element-1941" data-mp="1spz2fx4ab74z">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1942" data-mp="1srtqhkaurfda">
              Veelgestelde vragen over website beveiliging
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1943" data-mp="21q5i9pnggj7b">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-1944" data-mp="1wsdoeq392n0h" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1945" data-mp="2bikwqczjmpng">
                  <span className="font-bold text-navy-900" data-id="element-1946" data-mp="2kf28nifqerk9">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1947" data-mp="2z4dejgl51e7a" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1948" data-mp="343qbwv9my3o4" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1949" data-mp="2q5rxxqz88995">
                    {faq.a}
                  </div>}
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-1950" data-mp="2gbt0h5a3ldnj">
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-semibold px-6 py-3 rounded-button shadow-soft hover:shadow-medium transition-all duration-200 group" data-id="element-1951" data-mp="2l4m8c20v523u">
              Nog vragen? Neem contact op
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" data-id="element-1952" data-mp="22duxew1893nc" />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1953" data-mp="36xr4fjv5hjv" />
    </main>;
}